import _ from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";
import {
  AddContentToCourseRequest,
  Assessment,
  Course,
  CourseValidationError,
  CreateAssessmentRequest,
  CreateCourseErrorResponse,
  CreateCourseResponse,
  CreateQuestionInAssessmentRequest,
  DeleteCourseErrorResponse,
  DeleteCourseResponse,
  DeleteQuestionInAssessmentRequest,
  DraftCourseSummary,
  FetchCourseErrorResponse,
  FetchCourseResponse,
  FetchDraftCoursesResponse,
  FetchLessonForCourseErrorResponse,
  FetchLessonForCourseRequest,
  FetchLessonForCourseResponse,
  FetchLessonPreviewForCourseErrorResponse,
  FetchLessonPreviewForCourseRequest,
  FetchLessonPreviewForCourseResponse,
  FetchPublishedCoursesResponse,
  GenerateQuestionsErrorResponse,
  GenerateQuestionsRequest,
  GenerateQuestionsResponse,
  GenerateQuestionWebSocketResponse,
  PublishCourseErrorResponse,
  PublishCourseResponse,
  PublishedCourseSummary,
  Question,
  RearrangeEntitiesInCourseRequest,
  RearrangeQuestionsInAssessmentRequest,
  RemoveEntityFromCourseRequest,
  UpdateAssessmentPropertyRequest,
  UpdateCourseErrorResponse,
  UpdateCoursePropertyRequest,
  UpdateCourseRequest,
  UpdateCourseResponse,
  UpdateLessonPropertyRequest,
  UpdateValidationErrorsForCourseRequest,
  SearchCoursesRequest,
  SearchCoursesResponse,
  SearchCoursesErrorResponse,
  FetchPublicCourseRequest,
  FetchPublicCourseResponse,
  FetchPublicCourseErrorResponse,
  CreateCourseSectionRequest,
  UpdateCourseSectionPropertyRequest,
  FetchAttemptsForCourseRequest,
  FetchAttemptsForCourseResponse,
  FetchAttemptsForCourseErrorResponse,
  LearningRecordCourse,
  FetchCourseLearningRecordErrorResponse,
  FetchCourseLearningRecordResponse,
  FetchLearnersAssessmentReportRequest,
  FetchLearnersAssessmentReportResponse,
  FetchLearnersAssessmentReportErrorResponse,
  FetchPublishedPodcastResponse,
  FetchPublishedSimualtionResponse,
  PublishedSimulationSummary,
  PublishedPodcastSummary,
  SearchPublishedPodcastsResponse,
  SearchPublishedPodcastsErrorResponse,
  SearchResult,
  SearchPublishedPodcastsRequest,
  SearchPublishedSimulationsRequest,
  SearchPublishedSimulationsResponse,
  SearchPublishedSimulationsErrorResponse,
  CourseSkill,
  FetchSkillsForLearnerResponse,
  FetchSkillsForLearnerErrorResponse,
} from "src/types/coaching/courses";

import {
  ADD_CONTENT_TO_COURSE,
  CREATE_ASSESSMENT_REQUEST,
  CREATE_COURSE_REQUEST,
  CREATE_QUESTION_IN_ASSESSMENT_REQUEST,
  CREATING_COURSE_FAILED,
  CREATING_COURSE_SUCCEEDED,
  DELETE_COURSE_REQUEST,
  DELETE_QUESTION_IN_ASSESSMENT_REQUEST,
  DELETING_COURSE_FAILED,
  DELETING_COURSE_SUCCEEDED,
  DISCARD_COURSE_DRAFT_REQUEST,
  DISCARDING_COURSE_DRAFT_FAILED,
  DISCARDING_COURSE_DRAFT_SUCCEEDED,
  EDIT_COURSE_REQUEST,
  FETCH_COURSE_LEARNERS_REQUEST,
  FETCH_COURSE_REQUEST,
  FETCH_DRAFT_COURSE_REQUEST,
  FETCHING_COURSE_FAILED,
  FETCHING_COURSE_LEARNERS_FAILED,
  FETCHING_COURSE_LEARNERS_SUCCEEDED,
  FETCHING_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSE_FAILED,
  FETCHING_DRAFT_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSES_FAILED,
  FETCHING_DRAFT_COURSES_REQUEST,
  FETCHING_DRAFT_COURSES_SUCCEEDED,
  FETCHING_PUBLISHED_COURSES_FAILED,
  FETCHING_PUBLISHED_COURSES_REQUEST,
  FETCHING_PUBLISHED_COURSES_SUCCEEDED,
  GENERATE_QUESTIONS_REQUEST,
  GENERATING_QUESTIONS_FAILED,
  GENERATING_QUESTIONS_SUCCEEDED,
  INVALIDATE_COURSE_CREATION_STATE,
  INVALIDATE_COURSE_DELETION_STATE,
  INVALIDATE_COURSE_UPDATE_STATE,
  INVALIDATE_COURSES,
  INVALIDATE_GENERATE_QUESTIONS,
  PUBLISH_COURSE_REQUEST,
  PUBLISHING_COURSE_FAILED,
  PUBLISHING_COURSE_SUCCEEDED,
  REARRANGE_ENTITIES_IN_COURSE,
  REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST,
  REMOVE_ENTITY_FROM_COURSE,
  UPDATE_ASSESSMENT_PROPERTY_REQUEST,
  UPDATE_COURSE_PROPERTY_REQUEST,
  UPDATE_LESSON_PROPERTY_REQUEST,
  UPDATE_QUESTION_IN_ASSESSMENT_REQUEST,
  UPDATE_VALIDATION_ERRORS_FOR_COURSE,
  UPDATING_COURSE_FAILED,
  UPDATING_COURSE_REQUEST,
  UPDATING_COURSE_SUCCEEDED,
  WS_GENERATE_QUESTION_SYNC,
  INVALIDATE_PUBLISH_COURSE,
  UPDATING_SIMULATION_SUCCEEDED,
  DISCARDING_SIMULATION_DRAFT_SUCCEEDED,
  DELETING_SIMULATION_SUCCEEDED,
  ENROLL_COURSE_REQUEST,
  ENROLLING_COURSE_SUCCEEDED,
  ENROLLING_COURSE_FAILED,
  FETCH_LESSON_FOR_COURSE_REQUEST,
  FETCHING_LESSON_FOR_COURSE_SUCCEEDED,
  FETCHING_LESSON_FOR_COURSE_FAILED,
  FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED,
  WS_ASSET_PROCESSED,
  FETCH_PUBLIC_COURSE_REQUEST,
  FETCHING_PUBLIC_COURSE_SUCCEEDED,
  FETCHING_PUBLIC_COURSE_FAILED,
  CREATE_COURSE_SECTION_REQUEST,
  UPDATE_COURSE_SECTION_PROPERTY_REQUEST,
  SEARCH_COURSES_SSE_SUBSCRIBE,
  SEARCH_COURSES_SSE_MESSAGE_RECEIVED,
  SEARCH_COURSES_SSE_ERROR,
  SEARCH_COURSES_SSE_UNSUBSCRIBE,
  INVALIDATE_SSE_SEARCH_RESULTS,
  INVALIDATE_DRAFT_COURSE,
  FETCH_ATTEMPTS_FOR_COURSE_REQUEST,
  FETCHING_ATTEMPTS_FOR_COURSE_SUCCEEDED,
  FETCHING_ATTEMPTS_FOR_COURSE_FAILED,
  FETCH_COURSE_LEARNING_REQUEST,
  FETCHING_COURSE_LEARNING_RECORD_FAILED,
  FETCHING_COURSE_LEARNING_RECORD_SUCCEEDED,
  FETCH_LEARNERS_ASSESSMENT_REPORT_REQUEST,
  FETCHING_ASSESSMENT_REPORT_SUCCEEDED,
  FETCHING_ASSESSMENT_REPORT_FAILED,
  FETCHING_LEARNERS_ASSESSMENT_REPORT_SUCCEEDED,
  FETCHING_LEARNERS_ASSESSMENT_REPORT_FAILED,
  INVALIDATE_LEARNERS_ASSESSMENT_REPORT,
  DELETING_PODCAST_SUCCEEDED,
  FETCHING_PUBLISHED_SIMULATIONS_FAILED,
  FETCHING_PUBLISHED_SIMULATIONS_SUCCEEDED,
  FETCH_PUBLISHED_SIMULATIONS_REQUEST,
  FETCH_PUBLISHED_PODCASTS_REQUEST,
  FETCHING_PUBLISHED_PODCASTS_SUCCEEDED,
  FETCHING_PUBLISHED_PODCASTS_FAILED,
  SEARCH_PUBLISHED_PODCASTS_REQUEST,
  SEARCHING_PUBLISHED_PODCASTS_SUCCEEDED,
  SEARCHING_PUBLISHED_PODCASTS_FAILED,
  SEARCH_PUBLISHED_SIMULATIONS_REQUEST,
  SEARCHING_PUBLISHED_SIMULATIONS_SUCCEEDED,
  SEARCHING_PUBLISHED_SIMULATIONS_FAILED,
  INVALIDATE_PUBLISHED_PODCASTS,
  INVALIDATE_PUBLISHED_SIMULATIONS,
  FETCH_SKILLS_FOR_LEARNER_REQUEST,
  FETCHING_SKILLS_FOR_LEARNER_SUCCEEDED,
  FETCHING_SKILLS_FOR_LEARNER_FAILED,
} from "../../actions/actionTypes";

import { nanoid } from "nanoid";
import {
  EnrollCourseErrorResponse,
  EnrollCourseRequest,
  EnrollCourseResponse,
  FetchAssessmentReportResponse,
  FetchCourseLearnersRequest,
} from "src/types/coaching/learners";
import {
  DeleteSimulationResponse,
  Simulation,
  UpdateSimulationResponse,
} from "src/types/coaching/simulation";
import { validateCourseRequest } from "src/pages/coaching/manage/courses/edit/helper";
import { Asset, PreviewAsset } from "src/types/assets";
import { DeletePodcastResponse, Podcast } from "src/types/coaching/podcast";

interface CoursesState {
  drafts: DraftCourseItemsState;
  published: PublishedCourseItemsState;
  search: SearchCourseItemsState;
  loading: boolean;
  items: Course[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  creation: CourseCreationState | null;
  edit: CourseEditState | null;
  update: {
    [key: string]: CourseUpdateState;
  };
  publish: CoursePublishState | null;
  deletion: {
    [key: string]: CourseDeletionState;
  };
  course: {
    [key: string]: CourseState;
  };
  public: {
    [key: string]: PublicCourseState;
  };
  analytics: {
    recording: boolean;
    error: any;
  };
  session: {
    id: string | null;
    recording: boolean;
    error: any;
  };
  simulation: {
    loading: boolean;
    items: PublishedSimulationSummary[];
    pagination: {
      currentPage: number;
      totalPages: number;
      recordsPerPage: number;
      totalRecordsCount: number;
      hasMoreItems: boolean;
    };
    error: any;
  };
  podcast: {
    loading: boolean;
    items: PublishedPodcastSummary[];
    pagination: {
      currentPage: number;
      totalPages: number;
      recordsPerPage: number;
      totalRecordsCount: number;
      hasMoreItems: boolean;
    };
    error: any;
  };
  learners: {
    loading: boolean;
    loaded: boolean;
    items: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      recordsPerPage: number;
      totalRecordsCount: number;
      hasMoreItems: boolean;
    };
    error: any;
  };
  learnerReport: {
    loading: boolean;
    loaded: boolean;
    item: LearningRecordCourse | null;
    reports: {
      [sessionId: string]: {
        fetchingReport: boolean;
        fetchedReport: boolean;
        report?: any;
        error?: any;
      }
    }
  }
  attemptLearners: {
    loading: boolean;
    loaded: boolean;
    items: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      recordsPerPage: number;
      totalRecordsCount: number;
      hasMoreItems: boolean;
    };
    error: any;
  },
  learnerSkills: {
    loading: boolean;
    loaded: boolean;
    items: CourseSkill[];
    error: any | null;
  };
  assessments: Assessment | null;
  updatedContent: any;
}
interface DraftCourseItemsState {
  loading: boolean;
  items: DraftCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface PublishedCourseItemsState {
  loading: boolean;
  items: PublishedCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface SearchCourseItemsState {
  loading: boolean;
  message?: any;
  session?: string | null;
  items: DraftCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  published: {
    query: string;
    podcast: SearchResult<PublishedPodcastSummary>;
    simulation: SearchResult<PublishedSimulationSummary>;
  };
}

interface EnrollCourseState {
  id?: string;
  enrolling: boolean;
  enrolled: boolean;
  error: any;
}

interface CourseState {
  loading: boolean;
  loaded: boolean;
  item: Course | null;
  enroll: EnrollCourseState | null;
  error: any;
  generateQuestions: GenerateQuestionsState | null;
}

interface PublicCourseState {
  loading: boolean;
  loaded: boolean;
  item: Course | null;
  error: any;
  enroll: EnrollCourseState | null;
}

interface AssetState {
  loading: boolean;
  item: Asset | null;
  preview: {
    loading: boolean;
    item: PreviewAsset | null;
    error: any;
  };
  error: any;
}

interface CourseEditState {
  item: Course;
  errors: CourseValidationError[];
  assets: {
    [key: string]: AssetState;
  };
}

interface CourseCreationState {
  creating: boolean;
  created: boolean;
  item: Course | null;
  error: any;
}

interface CourseUpdateState {
  id: string;
  updating: boolean;
  updated: boolean;
  item: Course | null;
  error: any;
}

interface CoursePublishState {
  id: string;
  publishing: boolean;
  published: boolean;
  item: Course | null;
  error: any;
}

interface CourseDeletionState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

interface GenerateQuestionsState {
  generating: boolean;
  generated: boolean;
  assets: GenerateQuestionsForAssetState[];
  error: any;
}

interface GenerateQuestionsForAssetState {
  generating: boolean;
  generated: boolean;
  processed: boolean;
  asset: string;
  questions: Question[];
  status: string;
  error: any;
}

interface Report {
  fetchingReport: boolean;
  fetchedReport: boolean;
  report?: any;
  error?: any;
}

interface LearnerReports {
  [sessionId: string]: Report;
}

interface LearnerReportState {
  loading: boolean;
  loaded: boolean;
  item: LearningRecordCourse | null;
  reports: LearnerReports;
}

const initialDraftCourseItemsState: DraftCourseItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialPublishedCourseItemsState: PublishedCourseItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialSearchCourseItemsState: SearchCourseItemsState = {
  loading: false,
  message: null,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  published: {
    query: "",
    podcast: {
      loading: false,
      items: [],
      pagination: {
        currentPage: 0,
        totalPages: 0,
        recordsPerPage: 0,
        totalRecordsCount: 0,
        hasMoreItems: false,
      },
      error: null,
    },
    simulation: {
      loading: false,
      items: [],
      pagination: {
        currentPage: 0,
        totalPages: 0,
        recordsPerPage: 0,
        totalRecordsCount: 0,
        hasMoreItems: false,
      },
      error: null,
    },
  },
  
};

const initialLearnerReportState: LearnerReportState = {
  loading: false,
  loaded: false,
  item: null,
  reports: {},
};

const initialState: CoursesState = {
  drafts: initialDraftCourseItemsState,
  published: initialPublishedCourseItemsState,
  search: initialSearchCourseItemsState,
  loading: false,
  items: [],
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  error: null,
  creation: null,
  edit: null,
  update: {},
  publish: null,
  deletion: {},
  course: {},
  public: {},
  analytics: {
    recording: false,
    error: null,
  },
  session: {
    id: null,
    recording: false,
    error: null,
  },
  simulation: {
    loading: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  podcast: {
    loading: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  learners: {
    loading: false,
    loaded: false,
    items: [],
    pagination: {
      currentPage: -1,
      totalPages: 0,
      recordsPerPage: 20,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  learnerReport: initialLearnerReportState,
  attemptLearners: {
    loading: false,
    loaded: false,
    items: [],
    pagination: {
      currentPage: -1,
      totalPages: 0,
      recordsPerPage: 20,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  learnerSkills: {
    loading: false,
    loaded: false,
    items: [],
    error: null,
  },
  // assessments to be moved to inside course
  assessments: null,
  updatedContent: null,
};

const initialCourseCreationState: CourseCreationState = {
  creating: false,
  created: false,
  item: null,
  error: null,
};

const initialCourseUpdateState: CourseUpdateState = {
  id: "",
  updating: false,
  updated: false,
  item: null,
  error: null,
};

const initialCourseDeletionState: CourseDeletionState = {
  id: "",
  deleting: false,
  deleted: false,
  error: null,
};

const initialCoursePublishState: CoursePublishState = {
  id: "",
  publishing: false,
  published: false,
  item: null,
  error: null,
};

const addContentToCourseOrSection = (
  contents: any[],
  newContent: any,
  parent?: string | null
) => {
  let newContents = [...contents];
  if (!parent) {
    // No parent, add to course contents
    const courseContents = newContents.filter(c => !c.parent);
    newContent.order = newContent.order === -1 ? courseContents.length + 1 : newContent.order;

    newContents = [...newContents, newContent];
    newContents = _.sortBy(newContents, "order", "asc");
    newContents = sortContentsInCourse(newContents);
  } else {
    // Find the section with matching uuid
    newContents = newContents.map((content) => {
      if (content.entityType === "section" && content.uuid === parent) {
        const sectionContents = content.contents || [];
        newContent.order = newContent.order === -1 ? sectionContents.length + 1 : newContent.order;

        return {
          ...content,
          contents: [...sectionContents, newContent],
        };
      }
      return content;
    });

    newContents = _.sortBy(newContents, "order", "asc");
    newContents = sortContentsInCourse(newContents);
  }

  return newContents;
};

const removeContentFromCourse = (
  contents: any[],
  parent: string | null,
  content: string
) => {
  let newContents = [...contents];
  if (!parent) {
    // No parent, add to course contents
    newContents = newContents.filter((c) => c.uuid !== content);
    newContents = _.sortBy(newContents, "order", "asc");
    newContents = sortContentsInCourse(newContents);
  } else {
    // Find the section with matching uuid
    newContents = newContents.map((item) => {
      if (item.entityType === "section" && item.uuid === parent) {
        let sectionContents = item.contents || [];
        sectionContents = sectionContents.filter((sc) => sc.uuid !== content);
        sectionContents = _.sortBy(sectionContents, "order", "asc");
        sectionContents = sortContentsInCourse(sectionContents);

        return {
          ...item,
          contents: sectionContents,
        };
      }
      return item;
    });
  }

  return newContents;
};

const sortContentsInCourse = (contents: any[]) => {
  const newContents = _.map(contents, (content, index) => {
    return {
      ...content,
      order: index + 1,
    };
  });

  return newContents;
};

const findContentInCourse = (
  contents: any[],
  content: string,
  parent?: string | null
) => {

  if (!parent) {
    return contents.find((c) => c.uuid === content);
  } else {
    // Find the section with matching uuid
    const section = contents.find((c) => c.uuid === parent);
    if (section) {
      return (section.contents || []).find((sc) => sc.uuid === content);
    }
  }

  return null;
};

export function groupContentsBySection(contents: any[] = []): any[] {
  const contentMap: { [key: string]: any } = {};

  contents.forEach((item) => {
    contentMap[item.uuid] = { ...item, contents: [] };
  });

  const fetchContents = (parentId: string | null = null): any[] => {
    return contents
      .filter(item => (!parentId && !item.parent) || (item.parent === parentId))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) // Sort by order
      .map(item => {
        const node = contentMap[item.uuid];
        node.contents = fetchContents(item.uuid);
        return node;
      });
  }

  return fetchContents(null);
}

export function coursesReducer(
  state: CoursesState = initialState,
  action: UnknownAction,
): CoursesState {
  switch (action.type) {
    case FETCHING_DRAFT_COURSES_REQUEST: {
      return {
        ...state,
        drafts: {
          ...state.drafts,
          loading: true,
        },
      };
    }

    case FETCHING_DRAFT_COURSES_SUCCEEDED: {
      const { response } = action.payload as FetchDraftCoursesResponse;

      const existingItems: DraftCourseSummary[] = state.drafts.items;

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        drafts: {
          ...state.drafts,
          loading: false,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_DRAFT_COURSES_FAILED: {
      const error = action.payload;
      return {
        ...state,
        drafts: {
          ...state.drafts,
          loading: false,
          error: error,
        },
      };
    }

    case FETCHING_PUBLISHED_COURSES_REQUEST: {
      return {
        ...state,
        published: {
          ...state.published,
          loading: true,
        },
      };
    }

    case FETCHING_PUBLISHED_COURSES_SUCCEEDED: {
      const { response } = action.payload as FetchPublishedCoursesResponse;

      const existingItems: PublishedCourseSummary[] = state.published.items;

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        published: {
          ...state.published,
          loading: false,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_PUBLISHED_COURSES_FAILED: {
      const error = action.payload;
      return {
        ...state,
        published: {
          ...state.published,
          loading: false,
          error: error,
        },
      };
    }

    case CREATE_COURSE_REQUEST: {
      return {
        ...state,
        creation: {
          ...initialCourseCreationState,
          creating: true,
        },
      };
    }

    case CREATING_COURSE_SUCCEEDED: {
      const { response } = action.payload as CreateCourseResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      let records: DraftCourseSummary[] = state.drafts.items || [];

      records = [
        {
          id: response.id,
          entityType: response.entityType,
          course: response,
        },
        ...records,
      ];

      records = _.uniqBy(records, "id");

      return {
        ...state,
        drafts: {
          ...state.drafts,
          items: records,
        },
        creation: {
          ...creationState,
          creating: false,
          created: true,
          item: response,
          error: null,
        },
      };
    }

    case CREATING_COURSE_FAILED: {
      const { error } = action.payload as CreateCourseErrorResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          error: error,
        },
      };
    }

    case UPDATING_COURSE_REQUEST: {
      const { id } = action.payload as UpdateCourseRequest;
      let updateState = state.update[id] || initialCourseUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        update: {
          ...state.update,
          [id]: updateState,
        },
      };
    }

    case DISCARD_COURSE_DRAFT_REQUEST: {
      const id = action.payload as string;
      let updateState = state.update[id] || initialCourseUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        update: {
          ...state.update,
          [id]: updateState,
        },
      };
    }

    case UPDATING_COURSE_SUCCEEDED:
    case DISCARDING_COURSE_DRAFT_SUCCEEDED: {
      const { response } = action.payload as UpdateCourseResponse;
      const id = response.id;
      if (!id) {
        return state;
      }

      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      const courseState = state.course[id];

      const groupedContents = groupContentsBySection(response.contents || []);

      const updatedResponse = {
        ...response,
        contents: groupedContents,
      };


      const items = state.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      return {
        ...state,
        items,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: true,
            item: updatedResponse,
            error: null,
          },
        },
        course: {
          ...state.course,
          [id]: {
            ...courseState,
            item: updatedResponse,
          },
        },
        edit: {
          ...(state.edit || {
            item: null,
            errors: [],
            assets: {},
          }),
          item: updatedResponse,
          errors: [],
        },
      };
    }

    case UPDATING_COURSE_FAILED:
    case DISCARDING_COURSE_DRAFT_FAILED: {
      const { id, error } = action.payload as UpdateCourseErrorResponse;
      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: false,
            error: error,
          },
        },
      };
    }

    case DELETE_COURSE_REQUEST: {
      const id = action.payload as string;
      let deletionState = state.deletion[id] || initialCourseDeletionState;

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null,
      };

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: deletionState,
        },
      };
    }

    case DELETING_COURSE_SUCCEEDED: {
      const { id } = action.payload as DeleteCourseResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.published.items?.filter((item) => {
        return item.id !== id;
      });

      const drafts = state.drafts.items?.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        published: {
          ...state.published,
          items: items,
        },
        drafts: {
          ...state.drafts,
          items: drafts,
        },
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_COURSE_FAILED: {
      const { id, error } = action.payload as DeleteCourseErrorResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error: error,
          },
        },
      };
    }

    case PUBLISH_COURSE_REQUEST: {
      const id = action.payload as string;
      return {
        ...state,
        publish: {
          ...initialCoursePublishState,
          id,
          publishing: true,
        },
      };
    }

    case PUBLISHING_COURSE_SUCCEEDED: {
      const { response } = action.payload as PublishCourseResponse;
      const publishState = state.publish;
      if (!publishState) {
        return state;
      }

      if (publishState.id !== response.id) {
        return state;
      }

      const groupedContents = groupContentsBySection(response.contents || []);
      const updatedResponse = {
        ...response,
        contents: groupedContents,
      };

      let editState = state.edit;
      if (editState) {
        const { status, hasChanges, lastPublished, duration, publishedBy } = updatedResponse;
        editState = {
          ...editState,
          item: {
            ...editState.item,
            status,
            hasChanges,
            lastPublished,
            duration,
            publishedBy
          },
        };
      }

      return {
        ...state,
        publish: {
          ...publishState,
          publishing: false,
          published: true,
          item: updatedResponse,
          error: null,
        },
        edit: editState,
      };
    }


    case PUBLISHING_COURSE_FAILED: {
      const { id, error } = action.payload as PublishCourseErrorResponse;
      const publishState = state.publish;
      if (!publishState) {
        return state;
      }

      if (publishState.id !== id) {
        return state;
      }

      return {
        ...state,
        publish: {
          ...publishState,
          publishing: false,
          published: false,
          error: error,
        },
      };
    }

    case INVALIDATE_PUBLISH_COURSE: {
      return {
        ...state,
        publish: null,
      };
    }

    case INVALIDATE_COURSES: {
      return {
        ...state,
        loading: false,
        items: [],
        pagination: {
          currentPage: -1,
          totalPages: 0,
          recordsPerPage: 10,
          totalRecordsCount: 0,
          hasMoreItems: false,
        },
        error: null,
      };
    }

    case INVALIDATE_COURSE_CREATION_STATE: {
      return {
        ...state,
        creation: null,
      };
    }

    case INVALIDATE_COURSE_UPDATE_STATE: {
      return {
        ...state,
        update: {},
      };
    }

    case INVALIDATE_COURSE_DELETION_STATE: {
      const id = action.payload as string;
      const deletionState = _.omit(state.deletion, id);

      return {
        ...state,
        deletion: deletionState,
      };
    }

    case FETCH_COURSE_REQUEST:
    case FETCH_DRAFT_COURSE_REQUEST: {
      const id = action.payload as string;

      const courseState = state.course[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        course: {
          ...state.course,
          [id]: {
            ...courseState,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_COURSE_SUCCEEDED:
    case FETCHING_DRAFT_COURSE_SUCCEEDED: {
      const { response } = action.payload as FetchCourseResponse;

      if (!response || !response.id) {
        return state;
      }

      const courseState = state.course[response.id];
      if (!courseState) {
        return courseState;
      }

      const groupedContents = groupContentsBySection(response.contents || []);

      const updatedResponse = {
        ...response,
        contents: groupedContents,
      };

      return {
        ...state,
        course: {
          ...state.course,
          [response.id]: {
            ...courseState,
            loading: false,
            loaded: true,
            item: {
              ...updatedResponse,
            },
            error: null,
          },
        },
      };
    }

    case FETCHING_COURSE_FAILED:
    case FETCHING_DRAFT_COURSE_FAILED: {
      const { id, error } = action.payload as FetchCourseErrorResponse;

      const courseState = state.course[id];
      if (!courseState) {
        return state;
      }

      return {
        ...state,
        course: {
          ...state.course,
          [id]: {
            ...courseState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_DRAFT_COURSE: {
      const id = action.payload as string;
      let courseState = state.course;
      if (courseState[id]) {
        courseState = _.omit(courseState, id);
      }

      let editState = state.edit;
      if (editState && editState.item?.id === id) {
        editState = null;
      }

      let updateState = state.update;
      if (updateState[id]) {
        updateState = _.omit(updateState, id);
      }

      return {
        ...state,
        course: {
          ...courseState,
        },
        update: {
          ...updateState,
        },
        edit: editState,
      };
    }

    case EDIT_COURSE_REQUEST: {
      const course = action.payload as Course;

      return {
        ...state,
        edit: {
          item: course,
          errors: [],
          assets: {},
        },
      };
    }

    case UPDATE_VALIDATION_ERRORS_FOR_COURSE: {
      const { course, errors } =
        action.payload as UpdateValidationErrorsForCourseRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          errors: errors,
        },
      };
    }

    case ADD_CONTENT_TO_COURSE: {
      const { id, content, order, parent,entityType } = action.payload as AddContentToCourseRequest;
      const editState = state.edit;
      if (!editState || editState.item.id !== id) return state;

      const contents = _.get(editState, "item.contents") || [];

      const newContent = {
        uuid: nanoid(6),
        entityType: entityType,
        name: content.name,
        description: content.summary,
        parent: parent || null,
        order: -1,
        content: content,
      };

      const newContents = addContentToCourseOrSection(contents, newContent, parent);

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case FETCH_LESSON_FOR_COURSE_REQUEST: {
      const { course, asset } = action.payload as FetchLessonForCourseRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      let assetState = editState.assets[asset];
      if (!assetState) {
        assetState = {
          loading: false,
          item: null,
          error: null,
          preview: {
            loading: false,
            item: null,
            error: null,
          },
        };
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset]: {
              ...assetState,
              loading: true,
            },
          },
        },
      };
    }

    case FETCHING_LESSON_FOR_COURSE_SUCCEEDED: {
      const { course, asset } = action.payload as FetchLessonForCourseResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const assetState = editState.assets[asset.id];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset.id]: {
              ...assetState,
              loading: false,
              item: asset,
              error: null,
            },
          },
        },
      };
    }

    case FETCHING_LESSON_FOR_COURSE_FAILED: {
      const { course, asset, error } =
        action.payload as FetchLessonForCourseErrorResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const assetState = editState.assets[asset];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset]: {
              ...assetState,
              loading: false,
              item: null,
              error: error,
            },
          },
        },
      };
    }

    case FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST: {
      const { course, asset } =
        action.payload as FetchLessonPreviewForCourseRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const assetState = editState.assets[asset];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset]: {
              ...assetState,
              preview: {
                ...assetState.preview,
                loading: true,
              },
            },
          },
        },
      };
    }

    case FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED: {
      const { course, asset, preview } =
        action.payload as FetchLessonPreviewForCourseResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const assetState = editState.assets[asset];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset]: {
              ...assetState,
              preview: {
                ...assetState.preview,
                loading: false,
                item: preview,
                error: null,
              },
            },
          },
        },
      };
    }

    case FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED: {
      const { course, asset, error } =
        action.payload as FetchLessonPreviewForCourseErrorResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const assetState = editState.assets[asset];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset]: {
              ...assetState,
              preview: {
                ...assetState.preview,
                loading: false,
                item: null,
                error: error,
              },
            },
          },
        },
      };
    }

    case WS_ASSET_PROCESSED: {
      const asset = action.payload as Asset;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      const assetState = editState.assets[asset.id];
      if (!assetState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          assets: {
            ...editState.assets,
            [asset.id]: {
              ...assetState,
              loading: false,
              item: asset,
              error: null,
            },
          },
        },
      };
    }

    case REMOVE_ENTITY_FROM_COURSE: {
      const { course, parent, id } = action.payload as RemoveEntityFromCourseRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = removeContentFromCourse(contents, parent, id);

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case REARRANGE_ENTITIES_IN_COURSE: {
      const { id, source, target } = action.payload as RearrangeEntitiesInCourseRequest;

      const editState = state.edit;
      if (!editState || editState.item.id !== id) return state;

      const contents = _.get(editState, "item.contents") || [];
      let newContents = [...contents];
      const sourceParent = source.parent || null;
      const targetParent = target.parent || null;

      let element = findContentInCourse(newContents, source.id, sourceParent);
      if (!element) return state;

      element = { 
        ...element,
        parent: targetParent
      };

      if (sourceParent !== targetParent) {
        newContents = addContentToCourseOrSection(newContents, element, targetParent);
        newContents = removeContentFromCourse(newContents, sourceParent, source.id);
      } else {
        console.log("Rearranging within same parent");
        if (sourceParent) {
          const section = newContents.find(c => c.uuid === sourceParent);
          if (!section) return state;

          const sectionContents = [...(section.contents || [])];
          const sourceIndex = _.findIndex(sectionContents, { entityType: source.entityType, uuid: source.id });
          const targetIndex = _.findIndex(sectionContents, { entityType: target.entityType, uuid: target.id });
          if (sourceIndex === -1 || targetIndex === -1) return state;

          sectionContents.splice(sourceIndex, 1);
          sectionContents.splice(targetIndex, 0, element);

          const ordered = sortContentsInCourse(sectionContents);
          newContents = newContents.map(c => c.uuid === sourceParent ? { ...c, contents: ordered } : c);
        } else {
          const sourceIndex = _.findIndex(newContents, { entityType: source.entityType, uuid: source.id });
          const targetIndex = _.findIndex(newContents, { entityType: target.entityType, uuid: target.id });
          if (sourceIndex === -1) return state;
          
          newContents.splice(sourceIndex, 1);
          
          if (targetIndex === -1) newContents.push(element);
          else newContents.splice(targetIndex, 0, element);

          newContents = sortContentsInCourse(newContents);
        }
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case UPDATE_COURSE_PROPERTY_REQUEST: {
      const { course, property, value } =
        action.payload as UpdateCoursePropertyRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      let updatedItem = _.cloneDeep(editState.item);
      updatedItem = _.set(updatedItem, property, value);
      const errors = validateCourseRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors: errors,
        },
      };
    }

    case UPDATE_LESSON_PROPERTY_REQUEST: {
      const { course, lesson, property, value } =
        action.payload as UpdateLessonPropertyRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = contents.map((content) => {
        if (content.entityType === "lesson" && content.uuid === lesson) {
          return {
            ...content,
            [property]: value,
          };
        }

        if (content.entityType === "section") {
          const newSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "lesson" && c.uuid === lesson) {
              return { ...c, [property]: value };
            }
            return c;
          });
          return { ...content, contents: newSectionContents };
        }
        return content;
      });

      let updatedItem = _.cloneDeep(editState.item);
      updatedItem = _.set(updatedItem, "contents", newContents);
      const errors = validateCourseRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors: errors,
        },
      };
    }

    case CREATE_ASSESSMENT_REQUEST: {
      const { course, name, order, questions, parent } =
        action.payload as CreateAssessmentRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContent = {
        uuid: nanoid(6),
        entityType: "assessment",
        name: name,
        description: "",
        parent: parent || null,
        order: -1,
        questions: questions || [],
      };

      const newContents = addContentToCourseOrSection(contents, newContent, parent);

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case UPDATE_ASSESSMENT_PROPERTY_REQUEST: {
      const { course, assessment, property, value } =
        action.payload as UpdateAssessmentPropertyRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = contents.map((content) => {
        if (
          content.entityType === "assessment" &&
          content.uuid === assessment
        ) {
          return {
            ...content,
            [property]: value,
          };
        }

        if (content.entityType === "section") {
          // check inside section contents
          const updatedSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "assessment" && c.uuid === assessment) {
              return { ...c, [property]: value };
            }
            return c;
          });

          return { ...content, contents: updatedSectionContents };
        }

        return content;
      });

      let updatedItem = _.cloneDeep(editState.item);
      updatedItem = _.set(updatedItem, "contents", newContents);
      const errors = validateCourseRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors: errors,
        },
      };
    }

    case CREATE_QUESTION_IN_ASSESSMENT_REQUEST: {
      const { course, assessment, request, insertAt } =
        action.payload as CreateQuestionInAssessmentRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = contents.map((content) => {
        if (content.entityType === "assessment" && content.uuid === assessment) {
          // Top-level assessment
          let questions = [...(content.questions || [])];
          questions.splice(insertAt, 0, request);
          questions = questions.map((q, idx) => ({ ...q, order: idx }));
          return { ...content, questions };
        }

        if (content.entityType === "section") {
          // Check inside section contents
          const updatedSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "assessment" && c.uuid === assessment) {
              let questions = [...(c.questions || [])];
              questions.splice(insertAt, 0, request);
              questions = questions.map((q, idx) => ({ ...q, order: idx }));
              return { ...c, questions };
            }
            return c;
          });

          return { ...content, contents: updatedSectionContents };
        }

        return content;
      });

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }


    case UPDATE_QUESTION_IN_ASSESSMENT_REQUEST: {
      const { course, assessment, request } =
        action.payload as CreateQuestionInAssessmentRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = contents.map((content) => {
        if (
          content.entityType === "assessment" &&
          content.uuid === assessment
        ) {
          const questions = content.questions || [];
          let newQuestions = questions.map((question) => {
            if (question.uuid === request.uuid) {
              return request;
            }

            return question;
          });

          newQuestions = _.sortBy(newQuestions, "order", "asc");

          return {
            ...content,
            questions: newQuestions,
          };
        }

        if (content.entityType === "section") {
          // Section assessment
          const updatedSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "assessment" && c.uuid === assessment) {
              const questions = c.questions || [];
              let newQuestions = questions.map((q) =>
                q.uuid === request.uuid ? request : q
              );
              newQuestions = _.sortBy(newQuestions, "order", "asc");
              return { ...c, questions: newQuestions };
            }
            return c;
          });

          return { ...content, contents: updatedSectionContents };
        }


        return content;
      });

      let updatedItem = _.cloneDeep(editState.item);
      updatedItem = _.set(updatedItem, "contents", newContents);
      const errors = validateCourseRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors: errors,
        },
      };
    }

    case DELETE_QUESTION_IN_ASSESSMENT_REQUEST: {
      const { course, assessment, question } =
        action.payload as DeleteQuestionInAssessmentRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContents = contents.map((content) => {
        if (
          content.entityType === "assessment" &&
          content.uuid === assessment
        ) {
          const questions = content.questions || [];
          let newQuestions = questions.filter((q) => {
            return q.uuid !== question;
          });

          newQuestions = newQuestions.map((question, index) => {
            return {
              ...question,
              order: index,
            };
          });

          return {
            ...content,
            questions: newQuestions,
          };
        }

        if (content.entityType === "section") {
          // Section assessment
          const updatedSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "assessment" && c.uuid === assessment) {
              const questions = c.questions || [];
              let newQuestions = questions.filter((q) => q.uuid !== question);
              newQuestions = newQuestions.map((q, index) => ({ ...q, order: index }));
              return { ...c, questions: newQuestions };
            }
            return c;
          });
          return { ...content, contents: updatedSectionContents };
        }

        return content;
      });

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST: {
      const { course, assessment, source, target } =
        action.payload as RearrangeQuestionsInAssessmentRequest;

      const editState = state.edit;
      if (!editState) return state;
      if (editState.item.id !== course) return state;

      const contents = _.get(editState, "item.contents") || [];

      const newContents = contents.map((content) => {
        // Top-level assessment
        if (content.entityType === "assessment" && content.uuid === assessment) {
          const questions = content.questions || [];
          let newQuestions = [...questions];
          const sourceIndex = _.findIndex(questions, { uuid: source });
          const targetIndex = _.findIndex(questions, { uuid: target });

          const element = newQuestions[sourceIndex];
          newQuestions.splice(sourceIndex, 1);
          newQuestions.splice(targetIndex, 0, element);

          newQuestions = newQuestions.map((q, index) => ({ ...q, order: index }));

          return { ...content, questions: newQuestions };
        }

        // Section containing assessments
        if (content.entityType === "section") {
          const updatedSectionContents = (content.contents || []).map((c) => {
            if (c.entityType === "assessment" && c.uuid === assessment) {
              const questions = c.questions || [];
              let newQuestions = [...questions];
              const sourceIndex = _.findIndex(questions, { uuid: source });
              const targetIndex = _.findIndex(questions, { uuid: target });

              const element = newQuestions[sourceIndex];
              newQuestions.splice(sourceIndex, 1);
              newQuestions.splice(targetIndex, 0, element);

              newQuestions = newQuestions.map((q, index) => ({ ...q, order: index }));
              return { ...c, questions: newQuestions };
            }
            return c;
          });

          return { ...content, contents: updatedSectionContents };
        }

        return content;
      });

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }


    case GENERATE_QUESTIONS_REQUEST: {
      const { course, assets } = action.payload as GenerateQuestionsRequest;

      const courseState = state.course[course];
      if (!courseState) {
        return state;
      }

      const courseStateGenerateQuestions = assets.map((asset) => {
        return {
          generating: true,
          generated: false,
          processed: false,
          asset: asset,
          questions: [],
          status: "not_started",
          error: null,
        };
      });

      let existingItems = state.course[course].generateQuestions?.assets || [];
      existingItems = existingItems.filter((item) => {
        return !assets.includes(item.asset);
      });

      existingItems = [...existingItems, ...courseStateGenerateQuestions];

      return {
        ...state,
        course: {
          ...state.course,
          [course]: {
            ...courseState,
            generateQuestions: {
              generating: true,
              generated: false,
              assets: existingItems,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_QUESTIONS_SUCCEEDED: {
      const { course, responses } = action.payload as GenerateQuestionsResponse;

      const courseState = state.course[course];
      if (!courseState) {
        return state;
      }

      let existingItems = state.course[course].generateQuestions?.assets || [];
      existingItems = existingItems.map((asset) => {
        const response = responses.find((item) => item.asset === asset.asset);
        if (!response) {
          return asset;
        }

        return {
          generating: false,
          generated: true,
          processed: false,
          asset: response.asset,
          questions: response.questions,
          status: response.status,
          error: null,
        };
      });

      const generated = existingItems.every(
        (item) => item.status === "completed",
      );

      return {
        ...state,
        course: {
          ...state.course,
          [course]: {
            ...courseState,
            generateQuestions: {
              generating: !generated,
              generated: generated,
              assets: existingItems,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_QUESTIONS_FAILED: {
      const { course, error } =
        action.payload as GenerateQuestionsErrorResponse;

      const courseState = state.course[course];
      if (!courseState) {
        return state;
      }

      const generateQuestions = courseState.generateQuestions;
      if (!generateQuestions) {
        return state;
      }

      return {
        ...state,
        course: {
          ...state.course,
          [course]: {
            ...courseState,
            generateQuestions: {
              ...courseState!.generateQuestions!,
              generating: false,
              generated: false,
              error: error,
            },
          },
        },
      };
    }

    case WS_GENERATE_QUESTION_SYNC: {
      const { asset, status } =
        action.payload as GenerateQuestionWebSocketResponse;

      console.log("WS_GENERATE_QUESTION_SYNC", asset, status);

      let updatedCourseState = state.course;
      const keys = _.keys(state.course);
      keys.forEach((key) => {
        const courseState = state.course[key];

        const generateQuestions = courseState.generateQuestions;
        if (!generateQuestions) {
          return courseState;
        }

        const assets = generateQuestions.assets.map((item) => {
          if (item.asset === asset) {
            return {
              ...item,
              processed: status === "COMPLETED",
              status: status === "COMPLETED" ? "completed" : "in_progress",
            };
          }

          return item;
        });

        updatedCourseState = {
          [key]: {
            ...courseState,
            generateQuestions: {
              ...generateQuestions,
              assets: assets,
            },
          },
        };
      });

      return {
        ...state,
        course: updatedCourseState,
      };
    }

    case INVALIDATE_GENERATE_QUESTIONS: {
      const course = action.payload as string;

      const courseState = state.course[course];
      if (!courseState) {
        return state;
      }

      return {
        ...state,
        course: {
          ...state.course,
          [course]: {
            ...courseState,
            generateQuestions: null,
          },
        },
      };
    }

    case FETCH_COURSE_LEARNERS_REQUEST: {
      const { page } = action.payload as FetchCourseLearnersRequest;

      let existingItems: any[] = state.learners.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        learners: {
          ...state.learners,
          items: existingItems,
          loading: true,
          loaded: false,
        },
      };
    }

    case FETCHING_COURSE_LEARNERS_SUCCEEDED: {
      const { response } = action.payload as any;

      const existingItems: any[] = state.learners.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        learners: {
          ...state.learners,
          loading: false,
          loaded: true,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_COURSE_LEARNERS_FAILED: {
      const response = action.payload as any;
      return {
        ...state,
        learners: {
          ...state.learners,
          loading: false,
          loaded: false,
          error: response.error,
        },
      };
    }

    //Simulation related updates
    case UPDATING_SIMULATION_SUCCEEDED:
    case DISCARDING_SIMULATION_DRAFT_SUCCEEDED: {
      const { response } = action.payload as UpdateSimulationResponse;
      const id = response.id;
      if (!id) {
        return state;
      }

      const items = state.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      return {
        ...state,
        items,
      };
    }

    case DELETING_SIMULATION_SUCCEEDED: {
      const { id } = action.payload as DeleteSimulationResponse;

      const published = state.published.items?.filter((item) => {
        return item.id !== id;
      });

      const drafts = state.drafts.items?.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        published: {
          ...state.published,
          items: published,
        },
        drafts: {
          ...state.drafts,
          items: drafts,
        },
      };
    }

    case DELETING_PODCAST_SUCCEEDED: {
      const { id } = action.payload as DeletePodcastResponse;
    
      const published = state.published.items?.filter((item) => item.id !== id);
      const drafts = state.drafts.items?.filter((item) => item.id !== id);
    
      return {
        ...state,
        published: {
          ...state.published,
          items: published,
        },
        drafts: {
          ...state.drafts,
          items: drafts,
        },
      };
    }
    
    case ENROLL_COURSE_REQUEST: {
      const {course} = action.payload as EnrollCourseRequest;
      const id = course;
      const courseState = state.public[id];
      if (!courseState) {
        return state;
      }

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            enroll: {
              enrolling: true,
              enrolled: false,
              error: null,
            },
          },
        },
      };
    }

    case ENROLLING_COURSE_SUCCEEDED: {
      const { id, response } = action.payload as EnrollCourseResponse;
      const courseState = state.public[id];
      if (!courseState) {
        return state;
      }

      const enrollId = response.learningRecord.id;

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            enroll: {
              id: enrollId,
              enrolling: false,
              enrolled: true,
              error: null,
            },
          },
        },
      };
    }

    case ENROLLING_COURSE_FAILED: {
      const { id, error } = action.payload as EnrollCourseErrorResponse;
      const courseState = state.public[id];
      if (!courseState) {
        return state;
      }

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            enroll: {
              enrolling: false,
              enrolled: false,
              error: error,
            },
          },
        },
      };
    }
    
    case FETCH_PUBLIC_COURSE_REQUEST: {
      const { id } = action.payload as FetchPublicCourseRequest;
      const courseState = state.course[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_PUBLIC_COURSE_SUCCEEDED: {
      const { course, learningRecord } =
        action.payload as FetchPublicCourseResponse;

      if (!course || !course.id) {
        return state;
      }

      const id = course.id;
      const courseState = state.public[id];
      if (!courseState) {
        return courseState;
      }

      const groupedCourse = {
        ...course,
        contents: groupContentsBySection(course.contents || []),
      };

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            loading: false,
            loaded: true,
            item: groupedCourse,
            error: null,
            enroll: learningRecord ? {
              id: learningRecord.id,
              enrolling: false,
                enrolled: true, // set to true to indicate the course is already enrolled
                error: null,
              } : null,
            },
          },
        };
    }


    case FETCHING_PUBLIC_COURSE_FAILED: {
      const { id, error } = action.payload as FetchPublicCourseErrorResponse;
      const courseState = state.course[id];
      if (!courseState) {
        return state;
      }

      return {
        ...state,
        public: {
          ...state.public,
          [id]: {
            ...courseState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          },
        },
      };
    }

    case CREATE_COURSE_SECTION_REQUEST: {
      const { course, name, order } = action.payload as CreateCourseSectionRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== course) {
        return state;
      }

      const contents = _.get(editState, "item.contents") || [];
      const newContent = {
        uuid: nanoid(6),
        entityType: "section",
        name: name,
        description: "",
        order: order,
        contents: [],
      };

      const newContents = addContentToCourseOrSection(contents, newContent, null);

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            contents: newContents,
          },
        },
      };
    }

    case UPDATE_COURSE_SECTION_PROPERTY_REQUEST: {
      const { course, section, property, value } = action.payload as UpdateCourseSectionPropertyRequest;

      const editState = state.edit;
      if (!editState) return state;

      if (editState.item.id !== course) return state;

      const contents = _.get(editState, "item.contents") || [];

      const newContents = contents.map((item) => {
        if (item.entityType === "section" && item.uuid === section) {
          return {
            ...item,
            [property]: value,
            contents: item.contents || [],
          };
        }
        return item;
      });

      const updatedItem = {
        ...editState.item,
        contents: newContents,
      };

      const errors = validateCourseRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors,
        },
      };
    }

    case FETCH_ATTEMPTS_FOR_COURSE_REQUEST: {
      const { id } = action.payload as FetchAttemptsForCourseRequest;
    
      return {
        ...state,
        attemptLearners: {
          ...state.attemptLearners,
            loading: true,
            loaded: false,
            error: null,
          },
        };
      }
    

    case FETCHING_ATTEMPTS_FOR_COURSE_SUCCEEDED: {
      const { response } = action.payload as FetchAttemptsForCourseResponse;

      return {
        ...state,
        attemptLearners: {
          ...state.attemptLearners,
          loading: false,
          loaded: true,
          items: response,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_ATTEMPTS_FOR_COURSE_FAILED: {
      const response = action.payload as FetchAttemptsForCourseErrorResponse;
      return {
        ...state,
        attemptLearners: {
          ...state.attemptLearners,
          loading: false,
          loaded: false,
          error: response.error,
        },
      };
    }

    case FETCH_COURSE_LEARNING_REQUEST: {
      const id = action.payload as string;
    
      const recordState = state.learnerReport[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null
      };
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [id]: {
            ...recordState,
            loading: true,
            loaded: false,
            error: null,
          }
        }
      };
    }
    
    case FETCHING_COURSE_LEARNING_RECORD_SUCCEEDED: {
      const { response } = action.payload as FetchCourseLearningRecordResponse;
    
      if (!response || !response.id) {
        return state;
      }
    
      const recordState = state.learnerReport[response.id];
      if (!recordState) {
        return state;
      }
    
      const groupedContents = groupContentsBySection(response.course.contents || []);
    
      const updatedResponse = {
        ...response,
        course: {
          ...response.course,
          contents: groupedContents,
        }
      };
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [response.id]: {
            ...recordState,
            loading: false,
            loaded: true,
            item: updatedResponse,
          }
        }
      };
    }
    
    case FETCHING_COURSE_LEARNING_RECORD_FAILED: {
      const { id, error } = action.payload as FetchCourseLearningRecordErrorResponse;
    
      const recordState = state.learnerReport[id];
      if (!recordState) {
        return state;
      }
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [id]: {
            ...recordState,
            loading: false,
            loaded: false,
            item: null,
            error: error
          }
        }
      };
    }

    case FETCH_LEARNERS_ASSESSMENT_REPORT_REQUEST: {
      const { learnerRecordId, id } = action.payload as FetchLearnersAssessmentReportRequest;
      const recordState = state.learnerReport[learnerRecordId];
      if (!recordState?.item?.sessions) return state;
    
      const updatedSessions = recordState.item.sessions.map((session: any) => {
        if (session.id !== id) return session;
    
        return {
          ...session,
          fetchingReport: true,
          fetchedReport: false,
          fetchError: null,
        };
      });
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [learnerRecordId]: {
            ...recordState,
            item: {
              ...recordState.item,
              sessions: updatedSessions,
            },
          },
        },
      };
    }
    
    
    case FETCHING_LEARNERS_ASSESSMENT_REPORT_SUCCEEDED: {
      const { learningRecord, response } = action.payload as FetchLearnersAssessmentReportResponse;
      const recordState = state.learnerReport[learningRecord];
      if (!recordState?.item?.sessions) return state;
    
      const updatedSessions = recordState.item.sessions.map((session: any) => {
        if (session.id !== response.id) return session;
    
        return {
          ...session,
          fetchingReport: false,
          fetchedReport: true,
          report: response,
        };
      });
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [learningRecord]: {
            ...recordState,
            item: {
              ...recordState.item,
              sessions: updatedSessions,
            },
          },
        },
      };
    }
    
    
    case FETCHING_LEARNERS_ASSESSMENT_REPORT_FAILED: {
      const { learningRecord, id, error } = action.payload as FetchLearnersAssessmentReportErrorResponse;
      const recordState = state.learnerReport[learningRecord];
      if (!recordState?.item?.sessions) return state;
    
      const updatedSessions = recordState.item.sessions.map((session: any) => {
        if (session.id !== id) return session;
    
        return {
          ...session,
          fetchingReport: false,
          fetchedReport: false,
          error: error,
        };
      });
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [learningRecord]: {
            ...recordState,
            item: {
              ...recordState.item,
              sessions: updatedSessions,
            },
          },
        },
      };
    }

    case INVALIDATE_LEARNERS_ASSESSMENT_REPORT: {
      const { learningRecord, sessionId } = action.payload as any;
    
      const recordState = state.learnerReport[learningRecord];
      if (!recordState?.item?.sessions) return state;
    
      const updatedSessions = recordState.item.sessions.map((session: any) => {
        if (session.id !== sessionId) return session;
    
        return {
          ...session,
          fetchingReport: false,
          fetchedReport: false,
          report: undefined,
        };
      });
    
      return {
        ...state,
        learnerReport: {
          ...state.learnerReport,
          [learningRecord]: {
            ...recordState,
            item: {
              ...recordState.item,
              sessions: updatedSessions,
            },
          },
        },
      };
    }

        case SEARCH_COURSES_SSE_SUBSCRIBE: {

      return {
        ...state,
        search: {
          ...state.search,
          loading: true,
        },
      }
      
    }

    case SEARCH_COURSES_SSE_MESSAGE_RECEIVED: {

      const {type, response } = action.payload as any;

      if(type === "agent-text") {
        return {
          ...state,
          search: {
            ...state.search,
            loading: false,
            message: response,
          }
        }
      }

      if (type === "agent-response" && _.get(response, "courses")) {
        const session = response.session;
        const existingItems: DraftCourseSummary[] = state.search.items;
        let records: DraftCourseSummary[] = _.get(response, "courses") || [];

        const pagination = response.pagination;

        if (pagination.pageNumber > 0) {
          records = [...existingItems, ...records];
        }

        records = _.uniqBy(records, "id");

        return {
          ...state,
          search: {
            ...state.search,
            loading: false,
            session: session,
            items: records,
            pagination: {
              currentPage: pagination.pageNumber,
              totalPages: pagination.totalPagesCount,
              recordsPerPage: pagination.itemsPerPage,
              totalRecordsCount: pagination.totalItemCount,
              hasMoreItems: pagination.pageNumber < pagination.totalPagesCount - 1,
            },
          },
        };
      }

      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          items: [...state.search.items]
        },
      };
    }

    case SEARCH_COURSES_SSE_ERROR: {

      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          error: action.payload,
        }
      }

    }

    case SEARCH_COURSES_SSE_UNSUBSCRIBE: {

      return {
        ...state,
      }

    }

    case INVALIDATE_SSE_SEARCH_RESULTS: {
      return {
        ...state,
        search: {
          ...state.search,
          items: [],
          message: null,
          loading: false,
          error: null,
        },
      }
    }

    case FETCH_PUBLISHED_SIMULATIONS_REQUEST: {
      return {
        ...state,
        simulation: {
          ...state.simulation,
          loading: true,
        },
      };
    }

  case FETCHING_PUBLISHED_SIMULATIONS_SUCCEEDED: {
  const payload = action.payload as FetchPublishedSimualtionResponse;
  const response = payload.response;

  const existingItems: PublishedSimulationSummary[] =
    state.simulation.items;

  let records: PublishedSimulationSummary[] = response.records;

  if (response.currentPage > 0) {
    records = [...existingItems, ...records];
  }

  records = _.uniqBy(records, "id");

  return {
    ...state,
    simulation: {
      ...state.simulation,
      loading: false,
      items: records,
      pagination: {
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        recordsPerPage: response.recordsPerPage,
        totalRecordsCount: response.totalRecordsCount,
        hasMoreItems: response.currentPage < response.totalPages - 1,
      },
    },
  };
}


    case FETCHING_PUBLISHED_SIMULATIONS_FAILED: {
      const { error } = action.payload as any; //type here
    
      return {
        ...state,
        simulation: {
          ...state.simulation,
          loading: false,
          error,
        },
      };
    }

    case FETCH_PUBLISHED_PODCASTS_REQUEST: {
      return {
        ...state,
        podcast: {
          ...state.podcast,
          loading: true,
        },
      };
    }

case FETCHING_PUBLISHED_PODCASTS_SUCCEEDED: {
  const payload = action.payload as FetchPublishedPodcastResponse;
  const response = payload.response;

  const existingItems = state.podcast.items;

  let records = response.records;

  if (response.currentPage > 0) {
    records = [...existingItems, ...records];
  }

  records = _.uniqBy(records, "id");

  return {
    ...state,
    podcast: {
      ...state.podcast,
      loading: false,
      items: records,
      pagination: {
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        recordsPerPage: response.recordsPerPage,
        totalRecordsCount: response.totalRecordsCount,
        hasMoreItems: response.currentPage < response.totalPages - 1,
      },
    },
  };
}

    case FETCHING_PUBLISHED_PODCASTS_FAILED: {
      const { error } = action.payload as any;
    
      return {
        ...state,
        podcast: {
          ...state.podcast,
          loading: false,
          error,
        },
      };
    }

    case SEARCH_PUBLISHED_PODCASTS_REQUEST: {
      const { query } = action.payload as SearchPublishedPodcastsRequest;
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            query,
            podcast: {
              ...state.search.published.podcast,
              loading: true,
              error: null,
            },
          },
        },
      };
    }

    case SEARCHING_PUBLISHED_PODCASTS_SUCCEEDED: {
      const { response } = action.payload as SearchPublishedPodcastsResponse;
    
      const existingItems = response.currentPage > 0 ? state.search.published.podcast.items : [];
    
      const records = _.uniqBy(
        [...existingItems, ...response.records],
        "id"
      );
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            podcast: {
              loading: false,
              error: null,
              items: records,
              pagination: {
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                recordsPerPage: response.recordsPerPage,
                totalRecordsCount: response.totalRecordsCount,
                hasMoreItems:
                  response.currentPage < response.totalPages - 1,
              },
            },
          },
        },
      };
    }
    
    case SEARCHING_PUBLISHED_PODCASTS_FAILED: {
      const { error } = action.payload as SearchPublishedPodcastsErrorResponse;
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            podcast: {
              ...state.search.published.podcast,
              loading: false,
              error,
            },
          },
        },
      };
    }

    case SEARCH_PUBLISHED_SIMULATIONS_REQUEST: {
      const { query } = action.payload as SearchPublishedSimulationsRequest;
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            query,
            simulation: {
              ...state.search.published.simulation,
              loading: true,
              error: null,
            },
          },
        },
      };
    }

    case SEARCHING_PUBLISHED_SIMULATIONS_SUCCEEDED: {
      const { response } = action.payload as SearchPublishedSimulationsResponse;
    
      const existingItems =
        response.currentPage > 0
          ? state.search.published.simulation.items
          : [];
    
      const records = _.uniqBy(
        [...existingItems, ...response.records],
        "id"
      );
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            simulation: {
              loading: false,
              error: null,
              items: records,
              pagination: {
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                recordsPerPage: response.recordsPerPage,
                totalRecordsCount: response.totalRecordsCount,
                hasMoreItems:
                  response.currentPage < response.totalPages - 1,
              },
            },
          },
        },
      };
    }

    case SEARCHING_PUBLISHED_SIMULATIONS_FAILED: {
      const { error } = action.payload as SearchPublishedSimulationsErrorResponse;
    
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            simulation: {
              ...state.search.published.simulation,
              loading: false,
              error,
            },
          },
        },
      };
    }

    case INVALIDATE_PUBLISHED_PODCASTS: {
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            podcast: {
              ...state.search.published.podcast,
              loading: false,
              items: [],
              pagination: {
                ...state.search.published.podcast.pagination,
                currentPage: 0,
                totalPages: 0,
                recordsPerPage: 0,
                totalRecordsCount: 0,
                hasMoreItems: false,
              },
              error: null,
            },
          },
        },
      };
    }
    
    case INVALIDATE_PUBLISHED_SIMULATIONS: {
      return {
        ...state,
        search: {
          ...state.search,
          published: {
            ...state.search.published,
            simulation: {
              ...state.search.published.simulation,
              loading: false,
              items: [],
              pagination: {
                ...state.search.published.simulation.pagination,
                currentPage: 0,
                totalPages: 0,
                recordsPerPage: 0,
                totalRecordsCount: 0,
                hasMoreItems: false,
              },
              error: null,
            },
          },
        },
      };
    }

    case FETCH_SKILLS_FOR_LEARNER_REQUEST: {
      return {
        ...state,
        learnerSkills: {
          ...state.learnerSkills,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    }

    case FETCHING_SKILLS_FOR_LEARNER_SUCCEEDED: {
      const { response } = action.payload as FetchSkillsForLearnerResponse;

      return {
        ...state,
        learnerSkills: {
          ...state.learnerSkills,
          loading: false,
          loaded: true,
          items: response,
          error: null,
        },
      };
    }

    case FETCHING_SKILLS_FOR_LEARNER_FAILED: {
      const { error } = action.payload as FetchSkillsForLearnerErrorResponse;

      return {
        ...state,
        learnerSkills: {
          ...state.learnerSkills,
          loading: false,
          loaded: false,
          error,
        },
      };
    }

    default: {
      return state;
    }
  }
}
